import { inject, Injectable, signal } from "@angular/core";
import { TelemetryService } from "../../../../../services/api/telemetry/telemetry.service";
import { TelemetryEntry, TelemetryResponse } from "../../../../../models/shared/telemetry";
import { DateTime } from "luxon";
import { forkJoin, Observable, Subject, switchMap } from "rxjs";
import { ChartChangeTimeEvent } from "../../../../../models/ui/chartData";
import { ASSET_TYPES } from "../../../../../enums/assetTypes.enum";
import { AssetService } from "../../../../../services/api/asset/asset.service";
import { BotevoDesignTokens } from "../../../../../../assets/themes/botevoPreset";
import { RealtimeTelemetryResponse } from "../../../../../models/ws/realtimeTelemetry";
import {
  TimeseriesSubscriptionHandle,
  TimeseriesWebsocketService,
} from "../../../../../services/websocket/timeseries-websocket/timeseries-websocket.service";

const TELEMETRY_KEYS = ["Display_Sensor_Temp", "Display_Sensor_Humi"];

export interface RealtimeDataPoint {
  datasetIndex: number;
  point: { x: string; y: number };
  replace?: boolean;
}

@Injectable()
export class HumidityStatisticsService {
  private readonly _lineChartHumidity = signal<any>(null);
  readonly lineChartHumidity = this._lineChartHumidity.asReadonly();

  private _realtimePoint$ = new Subject<RealtimeDataPoint>();
  readonly realtimePoint$ = this._realtimePoint$.asObservable();

  private telemetryService = inject(TelemetryService);
  private assetService = inject(AssetService);
  private timeseriesWebsocket = inject(TimeseriesWebsocketService);
  private timeseriesSubscription?: TimeseriesSubscriptionHandle;

  // bucket data for current hour
  private currentHourStart = 0;
  private hourHumidities: number[] = [];
  private hourTemperatures: number[] = [];

  private currentAsset: { id: string; assetType: string } | null = null;
  public lastAsset: { id: string; assetType: string } | null = null;
  private realtimeActive = false;

  /**
   * Loads the initial state for the selected asset and initializes realtime data
   * @param assetId The id for the asset that is to be initialied
   * @param assetType The type for the asset that is to be initialied
   */
  public loadInitialState = (assetId: string, assetType: string) => {
    const now = DateTime.now().toMillis();
    const startOfDay = DateTime.now().startOf("day").toMillis();
    this._lineChartHumidity.set(null);
    this.currentAsset = { id: assetId, assetType: assetType };

    if (assetType === ASSET_TYPES.BUILDING) {
      this.aggregateBuildingTelemetry(assetId, startOfDay, now);
      this.startRealtime();
    } else if (assetType === ASSET_TYPES.GATE_SYSTEM) {
      this.fetchGateTelemetry(assetId, startOfDay, now).subscribe((data) => this.extractHumidityLineChart(data));
      this.startRealtime();
    }
  };

  /**
   * Closes websocket connections and resets data
   */
  public cleanup() {
    this.stopRealtime();
    this.lastAsset = this.currentAsset;
    this.currentAsset = null;
  }

  /**
   * Checks whether the current time range includes live data
   * @param end The endpoint of the current time frame
   * @returns A boolean that indicates whether the timeframe is live
   */
  private isLiveRange(end: number): boolean {
    return end >= DateTime.now().toMillis();
  }

  /**
   * Starts realtime data by initializing websockets
   */
  private startRealtime() {
    if (!this.currentAsset) return;
    const { id, assetType } = this.currentAsset;
    if (assetType === ASSET_TYPES.BUILDING) {
      this.assetService.getAssets(ASSET_TYPES.GATE_SYSTEM, id, ASSET_TYPES.BUILDING).subscribe((gates) => {
        this.resetHourBucket();
        this.initRealtimeTelemetry(
          gates.map((gate) => gate.id!),
          (data) => this.handleBuildingRealtimeUpdate(data)
        );
      });
    } else if (assetType === ASSET_TYPES.GATE_SYSTEM) {
      this.initRealtimeTelemetry([id], (data) => this.handleRealtimeTelemetry(data));
    }
    this.realtimeActive = true;
  }

  /**
   * Stops realtime data by closing websocket connections
   */
  private stopRealtime() {
    this.timeseriesSubscription?.close();
    this.timeseriesSubscription = undefined;
    this.resetHourBucket();
    this.realtimeActive = false;
  }

  /**
   * Resets data for current hour
   */
  private resetHourBucket() {
    this.currentHourStart = 0;
    this.hourHumidities = [];
    this.hourTemperatures = [];
  }

  /**
   * Creates an Observable that emits the telemetry response for a single gate
   * @param assetId The id for the gate whose telemetry data is to be fetched
   * @param start The start of the timerange for which data is to be fetched
   * @param end The end of the timerange for which data is to be fetched
   * @returns An Observable that emits the telemetry response for a single gate
   */
  private fetchGateTelemetry(assetId: string, start: number, end: number): Observable<TelemetryResponse> {
    return this.telemetryService.getTimeseries(assetId, ASSET_TYPES.GATE_SYSTEM, TELEMETRY_KEYS.join(","), start, end);
  }

  /**
   * Collects and aggregates telemetry data for all gates of a bulding
   * @param assetId The id of the building whose data is to be fetched
   * @param start The start of the timerange for which data is to be fetched
   * @param end The end of the timerange for which data is to be fetched
   */
  private aggregateBuildingTelemetry(assetId: string, start: number, end: number) {
    this.assetService
      .getAssets(ASSET_TYPES.GATE_SYSTEM, assetId, ASSET_TYPES.BUILDING)
      .pipe(
        switchMap((gates) => {
          const telemetryRequests = gates.map((gate) => {
            return this.fetchGateTelemetry(gate.id!, start, end);
          });
          return forkJoin(telemetryRequests);
        })
      )
      .subscribe((allResponses) => {
        const averaged = this.averageTelemetryResponse(allResponses);
        this.extractHumidityLineChart(averaged);
        if (this.isLiveRange(end)) {
          this.fillHourBucket(allResponses);
        }
      });
  }

  /**
   * Fills the hour bucket with data of the current hour
   * @param responses All responses which entries are to be added to the hour bucket
   */
  private fillHourBucket(responses: TelemetryResponse[]) {
    const hourStart = DateTime.now().startOf("hour").toMillis();

    this.currentHourStart = hourStart;
    this.hourHumidities = [];
    this.hourTemperatures = [];

    for (const response of responses) {
      const humidityEntries = response.Display_Sensor_Humi;
      const temperatureEntries = response.Display_Sensor_Temp;

      if (humidityEntries && Array.isArray(humidityEntries)) {
        for (const entry of humidityEntries) {
          const bucket = DateTime.fromMillis(entry.timestamp).startOf("hour").toMillis();
          if (bucket === hourStart) {
            this.hourHumidities.push(entry.value);
          }
        }
      }

      if (temperatureEntries && Array.isArray(temperatureEntries)) {
        for (const entry of temperatureEntries) {
          const bucket = DateTime.fromMillis(entry.timestamp).startOf("hour").toMillis();
          if (bucket === hourStart) {
            this.hourTemperatures.push(entry.value);
          }
        }
      }
    }
  }

  /**
   * Calculates the average of the telemetry data provided
   * @param responses An array of telemetry responses that are to be averaged
   * @returns An averaged TelemetryResponse
   */
  private averageTelemetryResponse(responses: TelemetryResponse[]): TelemetryResponse {
    const result: TelemetryResponse = {} as TelemetryResponse;
    for (const key of TELEMETRY_KEYS) {
      const groupedByTime = new Map<number, number[]>();
      for (const response of responses) {
        if (!response[key] || !Array.isArray(response[key])) continue;
        const entries = response[key as keyof TelemetryResponse] as TelemetryEntry[];
        for (const entry of entries) {
          const bucket = DateTime.fromMillis(entry.timestamp).startOf("hour").toMillis();
          if (!groupedByTime.has(bucket)) {
            groupedByTime.set(bucket, []);
          }
          groupedByTime.get(bucket)!.push(entry.value);
        }
      }
      const averaged: TelemetryEntry[] = [];
      for (const [timestamp, values] of groupedByTime) {
        averaged.push({
          timestamp,
          value: values.reduce((sum, v) => sum + v, 0) / values.length,
        });
      }
      result[key] = averaged;
    }
    return result;
  }

  /**
   * Initializes realtime data for a single gate
   * @param assetId The id of the gate for which realtime data is to be initialized
   */
  private initRealtimeTelemetry(assetIds: string[], onMessage: (data: RealtimeTelemetryResponse) => void) {
    const descriptor = {
      entityType: ASSET_TYPES.GATE_SYSTEM,
      entityId: assetIds,
      keys: TELEMETRY_KEYS,
    };
    if (this.timeseriesSubscription) {
      this.timeseriesSubscription.replace(descriptor);
      return;
    }
    const subscription = this.timeseriesWebsocket.register(descriptor);
    this.timeseriesSubscription = subscription;
    subscription.messages$.subscribe({
      next: (incomingData) => {
        onMessage(incomingData.data);
      },
      error: () => undefined,
    });
  }

  /**
   * Handles new iconming telemetry entry by adding the new data point to the subject of data points
   * @param data The icoming telemetry entry that is to be added
   */
  private handleRealtimeTelemetry(data: RealtimeTelemetryResponse) {
    const humidityData = data["Display_Sensor_Humi"];
    const temperatureData = data["Display_Sensor_Temp"];
    if (humidityData) {
      this._realtimePoint$.next({
        datasetIndex: 0,
        point: {
          x: DateTime.fromMillis(humidityData.timestamp).toISO()!,
          y: humidityData.value,
        },
      });
    }

    if (temperatureData) {
      this._realtimePoint$.next({
        datasetIndex: 1,
        point: {
          x: DateTime.fromMillis(temperatureData.timestamp).toISO()!,
          y: temperatureData.value,
        },
      });
    }

    if (temperatureData && humidityData) {
      this._realtimePoint$.next({
        datasetIndex: 2,
        point: {
          x: DateTime.fromMillis(temperatureData.timestamp).toISO()!,
          y: this.calculateDewPoint(humidityData.value, temperatureData.value),
        },
      });
    }
  }

  /**
   * Handles incoming new telemetry entrys for buildings
   * @param data The incoming telemetryEntry
   */
  private handleBuildingRealtimeUpdate(data: RealtimeTelemetryResponse) {
    const hourStart = DateTime.now().startOf("hour").toMillis();
    const isNewHour = hourStart !== this.currentHourStart;
    if (isNewHour) {
      this.currentHourStart = hourStart;
      this.hourHumidities = [];
      this.hourTemperatures = [];
    }

    if (data["Display_Sensor_Humi"]) {
      const value = Number(data["Display_Sensor_Humi"].value);
      if (!isNaN(value)) this.hourHumidities.push(value);
    }
    if (data["Display_Sensor_Temp"]) {
      const value = Number(data["Display_Sensor_Temp"].value);
      if (!isNaN(value)) this.hourTemperatures.push(value);
    }

    const bucketISO = DateTime.fromMillis(hourStart).toISO()!;

    let avgHumidity;
    if (this.hourHumidities.length > 0) {
      avgHumidity = this.hourHumidities.reduce((s, v) => s + v, 0) / this.hourHumidities.length;
      this._realtimePoint$.next({
        datasetIndex: 0,
        point: { x: bucketISO, y: avgHumidity },
        replace: !isNewHour,
      });
    }

    let avgTemperature;
    if (this.hourTemperatures.length > 0) {
      avgTemperature = this.hourTemperatures.reduce((s, v) => s + v, 0) / this.hourTemperatures.length;
      this._realtimePoint$.next({
        datasetIndex: 1,
        point: { x: bucketISO, y: avgTemperature },
        replace: !isNewHour,
      });
    }

    if (avgTemperature && avgHumidity) {
      this._realtimePoint$.next({
        datasetIndex: 2,
        point: {
          x: bucketISO,
          y: this.calculateDewPoint(avgHumidity, avgTemperature),
        },
        replace: !isNewHour,
      });
    }
  }

  /**
   * Extracts chart data from telemetry response
   * @param data The telemetry response from which to extract
   */
  private extractHumidityLineChart(data: TelemetryResponse) {
    const chartDataHumidity = this.convertTimeseriesToChartData(data.Display_Sensor_Humi);
    const chartDataTemp = this.convertTimeseriesToChartData(data.Display_Sensor_Temp);
    let chartDataDewpoint: { x: string | null; y: number }[] | undefined;
    if (chartDataHumidity && chartDataTemp) {
      const tempByTime = new Map(chartDataTemp.map((e) => [e.x, e.y]));
      chartDataDewpoint = chartDataHumidity
        .filter((entry) => tempByTime.has(entry.x))
        .map((entry) => ({
          x: entry.x,
          y: this.calculateDewPoint(entry.y, tempByTime.get(entry.x)!),
        }));
    }
    this._lineChartHumidity.set([
      {
        data: chartDataHumidity,
        borderColor: BotevoDesignTokens.primitive?.blue?.[200],
        yAxisID: "y1",
      },
      {
        data: chartDataTemp,
        borderColor: BotevoDesignTokens.primitive?.red?.[500],
        yAxisID: "y2",
      },
      {
        data: chartDataDewpoint,
        borderColor: BotevoDesignTokens.primitive?.green?.[500],
        yAxisID: "y2",
      },
    ]);
  }

  /**
   * Calculates the dewpoint for given humidity and temperature
   * @param humidity Humidity for calculation (in %)
   * @param temperature Temperature for calculation (in C°)
   * @returns The dewpoint (in C°)
   */
  private calculateDewPoint(humidity: number, temperature: number) {
    // Validierung der Eingabewerte
    if (humidity <= 0 || humidity > 100) {
      throw new Error("Relative Luftfeuchtigkeit muss zwischen 0 und 100% liegen");
    }

    if (temperature < -45 || temperature > 60) {
      throw new Error("Temperatur muss zwischen -45°C und 60°C liegen");
    }

    // Berechnung von γ(T, RH)
    const gamma = (17.27 * temperature) / (237.7 + temperature) + Math.log(humidity / 100);

    // Berechnung des Taupunkts
    const dewPoint = (237.7 * gamma) / (17.27 - gamma);

    return dewPoint;
  }

  /**
   * Converts telemetry entries to chart data objects
   * @param data The data that is to be converted
   * @returns An Array of data for charts
   */
  private convertTimeseriesToChartData(data: TelemetryEntry[] | undefined) {
    if (!data) return undefined;
    const chartData = [...data]
      .sort((a, b) => a.timestamp - b.timestamp)
      .map((entry) => ({
        x: DateTime.fromMillis(entry.timestamp).toISO(),
        y: entry.value,
      }));
    return chartData;
  }

  /**
   * Updates the chart to fit a given time range
   * @param event The event that holds the time range
   * @param asset The asset for which new data is to be fetched
   */
  public updateChartForTimeRange(event: ChartChangeTimeEvent, asset: { id: string; assetType: string }) {
    this._lineChartHumidity.set(null);
    this.currentAsset = asset;
    if (asset.assetType === ASSET_TYPES.GATE_SYSTEM) {
      this.fetchGateTelemetry(asset.id, event.start, event.end).subscribe((data) =>
        this.extractHumidityLineChart(data)
      );
    } else if (asset.assetType === ASSET_TYPES.BUILDING) {
      this.aggregateBuildingTelemetry(asset.id, event.start, event.end);
    }

    if (this.isLiveRange(event.end)) {
      if (!this.realtimeActive) {
        this.startRealtime();
      }
    } else {
      if (this.realtimeActive) {
        this.stopRealtime();
      }
    }
  }
}
